import React, { useState } from 'react';
import { IBPGeneralData } from '../../..';
import ListingWidget from '@components/widgets/listing-widget/ListingWidget';
import {
  ActionTarget,
  IEnhancedMenuItem,
  IFilterModel,
} from '@components/table/types';
import { IListingData } from '@models/listing';
import { bpAssignedPLansHeaders } from './content';
import ToastErrorMessage from '@components/toast/ToastErrorMessage';
import { toast } from 'react-toastify';
import { getError } from '@utils/graph-utils';
import { isEmpty } from '@utils/validationUtils';
import { IConfirmation } from '@redux/confirmation/types';
import ToastSuccessMessage from '@components/toast/ToastSuccessMessage';
import { useAppDispatch } from '@redux/hooks';
import { OpenConfirmationAction } from '@redux/confirmation/actions';
import { useLazyQuery, useMutation } from '@apollo/client';
import {
  activateBusinessPartnerPlan,
  deactivateBusinessPartnerPlan,
  getBPLinesLov,
  getBusinessPartnerPlanList,
} from './queries';
// import InlineFilterWidget from '@components/widgets/custom-listing-filter/InlineFilterWidget';
// import { IAbstractRecord } from '@models';
// import { cloneDeep } from 'lodash';
import { IListingFilterWidgetSection } from '@components/widgets/listing-filter-widget';
import { mapToAssignedPlanListingData } from './utils';
import { DEFAULT_ERROR_TEXT } from '@utils';
import AssignPlanDrawer from './assign-plan-drawer/AssignPlanDrawer';
import CommissionDrawer from './commission-drawer/CommissionDrawer';
import { mapToDetailedSelection } from './commission-drawer/utils';

interface IAssignedPlansWidget {
  generalData: IBPGeneralData;
  tableKey: string;
  onSuccess: () => void;
}

const AssignedPlansWidget: React.FC<IAssignedPlansWidget> = ({
  generalData,
  tableKey,
  onSuccess,
}) => {
  const dispatch = useAppDispatch();

  // const [filterSections, setFilterSections] =
  //   useState<IListingFilterWidgetSection[]>();

  const [filterValues, setFilterValues] = useState<IFilterModel>({
    namedFilters: {
      line: [],
    },
    pagination: {
      pageSize: 10,
      pageNumber: 1,
    },
    descending: false,
    orderBy: 'Line_ExternalCode',
    searchKey: null,
  });

  const [tableData, setTableData] = useState<IListingData>({
    pagedItems: {},
    pageSize: 10,
    pageNumber: 1,
    totalCount: 0,
  });

  const [drawer, setDrawer] = useState<{
    selectedIds: string[];
    isOpen: boolean;
    type: 'addPlans' | 'planCommission';
  }>({
    selectedIds: [],
    isOpen: false,
    type: 'addPlans',
  });

  const [getAssignedPlansLazy, { loading }] = useLazyQuery(
    getBusinessPartnerPlanList
  );

  const [getBPLinesLazy] = useLazyQuery(getBPLinesLov, {
    variables: {
      selectedBusinessPartnerID: generalData.businessPartner.id,
    },
  });

  const [activateBusinessPartnerPlanAction] = useMutation(
    activateBusinessPartnerPlan
  );
  const [deactivateBusinessPartnerPlanAction] = useMutation(
    deactivateBusinessPartnerPlan
  );

  const activateAssignedPlan = (selectedIds: string[]) => {
    const confirmation: IConfirmation = {
      open: true,
      title: 'Activate',
      message: `Are you sure you want to activate the selected assigned plan(s)?`,
      callback: async () => {
        try {
          const result = await activateBusinessPartnerPlanAction({
            variables: {
              entityId: generalData.businessPartner.id,
              selectedBusinessPartnerPlanIds: selectedIds,
            },
            errorPolicy: 'all',
          });

          if (isEmpty(result.errors)) {
            onSuccess();
            toast.success(
              <ToastSuccessMessage>
                Assigned plan successfully activated.
              </ToastSuccessMessage>
            );
          } else {
            toast.error(
              <ToastErrorMessage>{getError(result)}</ToastErrorMessage>
            );
          }
        } catch (error) {
          toast.error(
            <ToastErrorMessage>{getError({ errors: error })}</ToastErrorMessage>
          );
        }
      },
      submitButtonText: 'Submit',
      cancelButtonText: 'Cancel',
    };

    dispatch(OpenConfirmationAction(confirmation));
  };

  const deactivateAssignedPlan = (selectedIds: string[]) => {
    const confirmation: IConfirmation = {
      open: true,
      title: 'Deactivate',
      message: `Are you sure you want to deactivate the selected assigned plan(s)?`,
      callback: async () => {
        try {
          const result = await deactivateBusinessPartnerPlanAction({
            variables: {
              entityId: generalData.businessPartner.id,
              selectedBusinessPartnerPlanIds: selectedIds,
            },
            errorPolicy: 'all',
            // refetchQueries: [getBusinessPartnerPlanList()],
          });

          if (isEmpty(result.errors)) {
            onSuccess();
            getListingData();
            toast.success(
              <ToastSuccessMessage>
                Assigned plan successfully deactivated.
              </ToastSuccessMessage>
            );
          } else {
            toast.error(
              <ToastErrorMessage>{getError(result)}</ToastErrorMessage>
            );
          }
        } catch (error) {
          toast.error(<ToastErrorMessage>{getError(error)}</ToastErrorMessage>);
        }
      },
      submitButtonText: 'Submit',
      cancelButtonText: 'Cancel',
    };

    dispatch(OpenConfirmationAction(confirmation));
  };

  // const getLovs = async () => {
  //   const assignedBP = await getBPLinesLazy();

  //   const lovs = extractAssignedBP(assignedBP.data);

  //   if (!Object.keys(lovs.line).length) {
  //     lovs.line = filterValues?.line;
  //   }

  //   return lovs;

  //   const savedFilters = cloneDeep(filterValues);
  //   setFilterValues(savedFilters);

  //   const lovs = await getLovs();

  //   const newFilterSections = filterSectionsContent(lovs, savedFilters);
  //   setFilterSections(newFilterSections);
  // };

  const getListingData = async (filter = filterValues) => {
    if (!filter) {
      return {};
    }

    return getAssignedPlansLazy({
      variables: {
        pagination: filter.pagination,
        selectedBusinessPartnerID: generalData.businessPartner.id,
        // selectedBusinessPartnerLineID: filter.namedFilters.line,
        Attribute: filter.orderBy || 'Line_ExternalCode',
        Descending: filter.descending,
        keywordSearch: filter.searchKey,
      },
      errorPolicy: 'all',
    })
      .then(({ data }) => {
        if (data) {
          const tableData = mapToAssignedPlanListingData(data);

          setTableData({
            ...tableData,
            pageNumber: tableData.pageNumber,
            pageSize: tableData.pageSize,
            totalCount: tableData.totalCount,
          });
        }
      })
      .catch(() => {
        toast.error(
          <ToastErrorMessage>{DEFAULT_ERROR_TEXT}</ToastErrorMessage>
        );
      });
  };

  function handlePageChange(filterModel: IFilterModel): Promise<void> {
    return new Promise<void>(() => getListingData(filterModel));
  }

  // const onFilterUpdate = async (v: IAbstractRecord) => {
  //   const newFilters = cloneDeep(v);
  //   setFilterValues(newFilters);
  //   getListingData();
  // };

  // const renderFilter = () => {
  //   return (
  //     filterSections && (
  //       <InlineFilterWidget
  //         name={''}
  //         filters={filterSections}
  //         onApplyFilter={(v) => {
  //           onFilterUpdate(v);
  //         }}
  //       ></InlineFilterWidget>
  //     )
  //   );
  // };

  const actions: IEnhancedMenuItem[] = [
    {
      name: 'addPlans',
      title: 'Add Plan(s)',
      isPrimary: false,
      onClick: () =>
        setDrawer({
          isOpen: true,
          selectedIds: [],
          type: 'addPlans',
        }),
    },
    {
      name: 'setCommission',
      title: 'Set Commission',
      isPrimary: false,
      supportBulk: true,
      displayActionPerRecord: true,
      target: ActionTarget.Entity,
      rowIndependant: false,
      onClick: (selectedIds: string[]) => {
        setDrawer({
          isOpen: true,
          selectedIds: selectedIds,
          type: 'planCommission',
        });
      },
    },
    {
      name: 'activatePlans',
      title: 'Activate Plan(s)',
      isPrimary: false,
      supportBulk: true,
      displayActionPerRecord: true,
      target: ActionTarget.Entity,
      rowIndependant: false,
      onClick: (selectedIds: string[]) => {
        activateAssignedPlan(selectedIds);
      },
    },
    {
      name: 'deactivate',
      title: 'Deactivate',
      isPrimary: false,
      supportBulk: true,
      displayActionPerRecord: true,
      target: ActionTarget.Entity,
      rowIndependant: false,
      onClick: (selectedIds: string[]) => {
        deactivateAssignedPlan(selectedIds);
      },
    },
  ];

  return (
    <>
      <ListingWidget
        key={tableKey}
        title=""
        inlineTitle="Assigned Plans"
        name="assignedPlans"
        orderByAscendingByDefault
        inline
        usePagination
        data={tableData}
        loading={loading}
        tableSettings={{
          headers: bpAssignedPLansHeaders,
        }}
        onPageChange={(filterModel) => {
          setFilterValues({
            ...filterModel,
            namedFilters: filterValues.namedFilters,
          });
          return handlePageChange(filterModel);
        }}
        actions={
          generalData.userIsAdmin && generalData.businessPartner.isActive
            ? actions
            : []
        }
        dropdownActionsNames={[
          'addPlans',
          'setCommission',
          'activatePlans',
          'deactivate',
        ]}
        // lineNameController={
        //   <>
        //     <div className={classes.container}>{renderFilter()}</div>
        //   </>
        // }
      />
      {drawer.isOpen && drawer.type === 'addPlans' && (
        <AssignPlanDrawer
          open={drawer.isOpen}
          businessPartnerId={generalData.businessPartner.id}
          isDirectSalesforce={generalData.businessPartner.isDirectSalesforce}
          selectedBusinessPlanIds={drawer.selectedIds}
          selectedDetailedSelection={
            tableData.pagedItems[drawer.selectedIds[0]]
          }
          onClose={() =>
            setDrawer({
              isOpen: false,
              selectedIds: [],
              type: 'addPlans',
            })
          }
          onSuccess={onSuccess}
        />
      )}
      {drawer.isOpen && drawer.type === 'planCommission' && (
        <CommissionDrawer
          open={drawer.isOpen}
          businessPartnerId={generalData.businessPartner.id}
          selectedPlans={drawer.selectedIds}
          selectedDetailedSelection={mapToDetailedSelection(
            tableData.pagedItems[drawer.selectedIds[0]]
          )}
          onClose={() =>
            setDrawer({
              isOpen: false,
              selectedIds: [],
              type: 'addPlans',
            })
          }
          onSuccess={onSuccess}
        />
      )}
    </>
  );
};

export default AssignedPlansWidget;
